/**
 * API Client with Automatic JWT Token Injection
 *
 * Centralized axios client that automatically:
 * - Injects JWT tokens from local storage into Authorization header
 * - Handles 401 errors with redirect to login
 * - Provides consistent error handling across all API requests
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken, clearAccessToken } from '../utils/auth-token';
import { showErrorToast } from '../utils/toast';

// API base URL from environment
const API_BASE_URL = (window as any).env?.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

/**
 * Axios client instance with automatic token injection
 */
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

/**
 * Request Interceptor: Inject JWT Token
 *
 * Automatically retrieves access_token from storage
 * into Authorization header for all requests
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const token = getAccessToken();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    } catch (error) {
      console.error('[API Client] ❌ Error injecting token:', error);
      return config; // Proceed without token on error
    }
  },
  (error) => {
    console.error('[API Client] ❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor: Handle 401 Errors
 *
 * If a request fails with 401 Unauthorized:
 * 1. Clear token
 * 2. Redirect to login
 * TODO: Implement Refresh Token logic with backend /refresh endpoint
 */
apiClient.interceptors.response.use(
  // Success - pass through
  (response) => response,

  // Error - handle 401
  async (error: AxiosError) => {
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      console.log('[API Client] 401 Unauthorized - clearing session and redirecting...');
      
      clearAccessToken();

      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        // preserve query params if needed, or just redirect
        window.location.href = '/login?reason=session_expired';
      }
      
      return Promise.reject(error);
    }

    // Handle 403 plan-limit errors from backend
    if (error.response?.status === 403) {
      const data = error.response?.data as any;
      if (data?.error_code === 'PLAN_LIMIT_EXCEEDED' || data?.error_code === 'FEATURE_NOT_AVAILABLE') {
        window.dispatchEvent(
          new CustomEvent('plan:limit-reached', {
            detail: { feature: data.feature, message: data.detail },
          })
        );
      }
    }

    // Handle 402 monthly-spend-cap refusals from the backend.
    // Mirrors the 403 plan-limit branch above: broadcast an event so the
    // persistent banner flips to "capped" the instant a session is refused,
    // instead of waiting for the next poll. The caller still gets the
    // rejection so it can render its own inline failure.
    if (error.response?.status === 402) {
      const data = error.response?.data as any;
      const detail = data?.detail;
      if (detail?.code === 'monthly_spend_cap_reached') {
        window.dispatchEvent(
          new CustomEvent('spend-cap:reached', {
            detail: {
              message: detail.message,
              capCents: detail.cap_cents,
              spentCents: detail.spent_cents,
              resetsAt: detail.resets_at,
            },
          })
        );
      }
    }

    // Not a 401 - show error toast for server errors (500+) and network errors
    const status = error.response?.status;
    if (!status || status >= 500 || !error.response) {
      const errorMessage = getApiErrorMessage(error);
      showErrorToast(errorMessage);
    }

    return Promise.reject(error);
  }
);

/**
 * Type-safe API response wrapper
 */
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  status: number;
}

/**
 * Type-safe API error
 */
export interface ApiError {
  detail: string;
  status: number;
  errors?: Record<string, string[]>;
}

/**
 * Helper: Extract error message from API error response
 *
 * Handles the shapes FastAPI emits for ``detail``:
 *   - string  ("Invalid PIN")       → return as-is
 *   - object  ({code, message})     → prefer .message / .detail, else stringify
 *   - array   ([{loc, msg, type}])  → Pydantic 422; join the .msg fields so
 *                                     React doesn't render "[object Object]"
 */
export const getApiErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as Partial<ApiError> | undefined;
    const detail = data?.detail as unknown;
    if (typeof detail === 'string' && detail) return detail;
    if (Array.isArray(detail)) {
      const joined = detail
        .map((d: any) => {
          if (typeof d === 'string') return d;
          if (d && typeof d === 'object' && typeof d.msg === 'string') {
            const loc = Array.isArray(d.loc) ? d.loc.join('.') : '';
            return loc ? `${loc}: ${d.msg}` : d.msg;
          }
          try {
            return JSON.stringify(d);
          } catch {
            return '';
          }
        })
        .filter(Boolean)
        .join('; ');
      if (joined) return joined;
    }
    if (detail && typeof detail === 'object') {
      const obj = detail as Record<string, unknown>;
      if (typeof obj.message === 'string') return obj.message as string;
      if (typeof obj.detail === 'string') return obj.detail as string;
      try {
        return JSON.stringify(obj);
      } catch {
        /* fall through */
      }
    }
    return error.message || 'An unexpected error occurred';
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
};

/**
 * Helper: Check if error is authentication-related
 */
export const isAuthError = (error: unknown): boolean => {
  if (axios.isAxiosError(error)) {
    return error.response?.status === 401 || error.response?.status === 403;
  }
  return false;
};

/**
 * Helper: Check if user is currently authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!getAccessToken();
};
